import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, MessageCircle, Instagram, Mail, Youtube } from "lucide-react";
import { CONTACTS, NAV_LINKS } from "../../lib/data";

const CHANNELS = [
  { key: "whatsapp", icon: MessageCircle, note: "Быстрый ответ в WhatsApp", ...CONTACTS.whatsapp },
  { key: "instagram", icon: Instagram, note: "Смотреть работы и сторис", ...CONTACTS.instagram },
  { key: "email", icon: Mail, note: "Напишите по электронной почте", ...CONTACTS.email },
  { key: "youtube", icon: Youtube, note: "Смотреть видео и проекты", ...CONTACTS.youtube },
];

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative pt-16 md:pt-24 px-6 md:px-12 border-t border-white/5 overflow-hidden"
    >
      <div className="max-w-3xl mx-auto pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <span className="section-eyebrow">Глава 05 — Контакты</span>
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.95] tracking-tight text-white mt-4">
            Давайте создадим
            <br />
            <span className="text-[#e63946]">крутой контент</span>
          </h2>
          <p className="font-editorial italic text-xl md:text-2xl text-white/50 mt-3">
            для вашего бренда
          </p>

          <p className="mt-6 max-w-lg font-body text-sm md:text-base text-white/60 leading-relaxed">
            Контент, который цепляет с первых секунд, вызывает эмоции и
            продаёт. Индивидуальный подход, внимание к деталям и премиальный
            результат.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="relative mt-8 flex items-start gap-4 overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] p-5 md:p-6"
        >
          <div className="pointer-events-none absolute right-[-8%] top-1/2 h-[125%] w-[48%] -translate-y-1/2 rounded-full bg-[#e63946]/30 blur-[90px]" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_82%_50%,rgba(230,57,70,0.18),transparent_42%)]" />

          <Sparkles className="relative z-10 w-6 h-6 text-[#e63946] shrink-0 mt-0.5" aria-hidden />
          <div className="relative z-10">
            <p className="font-display uppercase text-sm md:text-base text-white leading-snug">
              СОЗДАЮ ВИДЕО,{" "}
              <span className="text-[#e63946]">КОТОРЫЕ ЗАПОМИНАЮТСЯ</span>
            </p>
            <p className="font-body text-xs md:text-sm text-white/55 mt-2 leading-relaxed">
              Напишите мне — вместе создадим видео, которое хочется пересматривать.
            </p>
          </div>
        </motion.div>

        <div className="mt-8 flex flex-col gap-3">
          {CHANNELS.map((c, i) => (
            <motion.a
              key={c.key}
              href={c.href}
              target={c.key === "email" ? undefined : "_blank"}
              rel="noopener noreferrer"
              data-cursor="link"
              data-testid={`contact-${c.key}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="group flex items-center gap-4 border border-white/10 rounded-xl p-4 md:p-5 hover:bg-white/[0.03] transition-colors"
            >
              <span className="flex items-center justify-center w-11 h-11 md:w-12 md:h-12 rounded-lg bg-white/5 shrink-0">
                <c.icon className="w-5 h-5 text-[#e63946]" aria-hidden />
              </span>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="font-body text-[10px] tracking-[0.28em] uppercase text-white/40">
                  {c.label}
                </span>
                <span className="font-display uppercase text-lg md:text-xl text-white truncate">
                  {c.display}
                </span>
                <span className="font-body text-xs text-white/45 mt-0.5">
                  {c.note}
                </span>
              </div>
              <ArrowUpRight
                className="w-5 h-5 text-white/40 shrink-0 group-hover:text-[#e63946] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                aria-hidden
              />
            </motion.a>
          ))}
        </div>
      </div>

      {/* Footer */}
      <footer
        data-testid="site-footer"
        className="border-t border-white/5 py-10 md:py-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
      >
        <div className="flex items-center gap-3">
          <span className="rec-dot" aria-hidden />
          <span className="font-display text-xl tracking-wider">
            Y·A — Yernar Assanbekov
          </span>
        </div>

        <ul className="flex flex-wrap items-center gap-6 md:gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <a
                href={`#${l.id}`}
                data-cursor="link"
                data-testid={`footer-link-${l.id}`}
                className="font-body text-[10px] tracking-[0.28em] uppercase text-white/50 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-6 text-[10px] tracking-[0.28em] uppercase text-white/40 font-body">
          <a
            href="#privacy"
            data-cursor="link"
            data-testid="footer-privacy"
            className="hover:text-white transition-colors"
          >
            Политика конфиденциальности
          </a>
          <span>© MMXXVI · Kazakhstan</span>
        </div>
      </footer>
    </section>
  );
}
